import { useState, useEffect, useRef, useCallback } from 'react';
import { getArtistTopTracks, findArtistByName } from '../api/deezerClient';
import { getArtistInfo } from '../api/lastfmClient';
import { trackLinkKey } from '../api/itunesClient';
import { useAppleMusicSongLinks } from '../hooks/useAppleMusicSongLinks';
import { useAudioPreview } from '../hooks/useAudioPreview';
import { buildMusicSearchUrl, buildArtistPageUrl, DEFAULT_MUSIC_SERVICE, MUSIC_SERVICE_LABELS } from '../utils/exportUtils';
import { useAuth, useAuthActions } from '../state/AuthContext';
import ArtistActionPills from './ArtistActionPills';
import ActionPillsCallout from './ActionPillsCallout';
import MusicServiceIcon from './MusicServiceIcon';
import { ZONE_COLORS } from '../galaxy/collisionGraphBuilder';
import '../styles/panel.css';

// Module-level cache so enriched images persist across node switches
// and are instantly available when revisiting a previously-viewed artist.
const imageCache = new Map();

const ZONE_INFO = {
  core_overlap:       { label: 'Core Overlap',      desc: 'Artists you both know and love' },
  your_artists:       { label: 'Your Artists',       desc: 'Your exclusive favorites' },
  friend_artists:     { label: "Friend's Artists",   desc: 'Their exclusive favorites' },
  shared_frontier:    { label: 'Shared Frontier',    desc: 'New artists to explore together' },
  your_exploration:   { label: 'Incoming Signals',   desc: "Their artists that connect to yours" },
  friend_exploration: { label: 'Outgoing Signals',   desc: 'Your artists that connect to theirs' },
};

// Artists at or above this listener count are too popular for "Hidden Gem"
// to ring true. Mirrors HIDDEN_GEM_LISTENER_CAP in server/universeCompute.js.
const HIDDEN_GEM_LISTENER_CAP = 1_000_000;

function getBadgeInfo(node, listeners) {
  if (node.type === 'seed') return { label: 'Your Artist', className: 'badge-seed', color: '#FFD700' };
  if (node.isDrift || node.tier === 'drift') return { label: 'Drift', className: 'badge-drift', color: 'hsl(15, 65%, 60%)' };
  if (node.discoveryMethod === 'chain_bridge') return { label: 'Chain Bridge', className: 'badge-chain', color: '#b48cff' };
  if (node.tier === 'hidden_gem') {
    // Wait for the on-demand Last.fm listener fetch before committing to a
    // label — otherwise we'd briefly show "Hidden Gem" and then flip to
    // "Top Pick" for popular artists once the count arrives.
    const haveListeners = listeners != null || node.listeners != null;
    if (!haveListeners) {
      return { label: 'Hidden Gem', className: 'badge-gem', color: '#64dcc8', pending: true };
    }
    const knownListeners = listeners ?? node.listeners ?? 0;
    if (knownListeners >= HIDDEN_GEM_LISTENER_CAP) {
      return { label: 'Top Pick', className: 'badge-rec', color: 'var(--color-accent-hover)' };
    }
    return { label: 'Hidden Gem', className: 'badge-gem', color: '#64dcc8' };
  }
  return { label: 'Top Pick', className: 'badge-rec', color: 'var(--color-accent-hover)' };
}

function ArtistDetailPanel({ node, onClose, onQueueSeed, onUnqueueSeed, pendingSeeds = [] }) {
  const [allTracks, setAllTracks] = useState(null);
  const [loadingTracks, setLoadingTracks] = useState(false);
  const [listeners, setListeners] = useState(null);
  const [enrichedImage, setEnrichedImage] = useState(null);
  const [enrichedDeezerId, setEnrichedDeezerId] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [showScrollFade, setShowScrollFade] = useState(false);
  const panelRef = useRef(null);
  const audio = useAudioPreview();

  const { user, favorites, dislikes, knownArtists, discoveredArtists } = useAuth();
  const { recordArtistPanelOpened } = useAuthActions();
  const musicService = user?.musicService || DEFAULT_MUSIC_SERVICE;

  // Canonical Apple Music links, filled in as background lookups resolve
  const appleMusicLinks = useAppleMusicSongLinks(allTracks, musicService);

  // Reset scroll position, collapse state, and stop audio when node changes
  useEffect(() => {
    audio.pause();
    setExpanded(false);
    setAllTracks(null);
    setLoadingTracks(false);
    if (panelRef.current) panelRef.current.scrollTop = 0;
  }, [node]); // eslint-disable-line react-hooks/exhaustive-deps

  // Bump the running panel-opens counter that drives the action-pills
  // nudge. We only count while the nudge is still pending — once the
  // callout has been shown or the user has marked any artist, the count
  // no longer matters and we stop spending API calls on it.
  useEffect(() => {
    if (!node || !user) return;
    if (user.actionPillsCalloutShown) return;
    const hasMarks =
      favorites.length + dislikes.length + knownArtists.length + discoveredArtists.length > 0;
    if (hasMarks) return;
    recordArtistPanelOpened();
  }, [node]); // eslint-disable-line react-hooks/exhaustive-deps

  // Enrich image + Deezer ID from Deezer when the node doesn't have them.
  useEffect(() => {
    if (!node) return;
    setEnrichedDeezerId(null);

    // Skip lookup only when we already have both an image and a numeric ID
    if ((node.imageLarge || node.image) && /^\d+$/.test(node.id)) {
      setEnrichedImage(null);
      return;
    }

    // Check cache first — avoids placeholder flash for previously-viewed artists
    const nameKey = node.name.toLowerCase().trim();
    const cached = imageCache.get(nameKey);
    if (cached) {
      setEnrichedImage(cached.image);
      setEnrichedDeezerId(cached.deezerId);
      return;
    }

    // Don't clear enrichedImage here — keep the previous image visible
    // until the new one arrives to avoid a flash of the placeholder.
    // Priority: the user just selected this artist, so jump ahead of any
    // queued background enrichment (new releases, batch lookups).
    let cancelled = false;
    findArtistByName(node.name, { priority: true })
      .then((deezerArtist) => {
        if (!cancelled && deezerArtist) {
          const img = deezerArtist.imageLarge || deezerArtist.image || null;
          imageCache.set(nameKey, { image: img, deezerId: deezerArtist.id });
          // Write image back to the graph node so the canvas/playlist
          // can use it without another lookup.
          if (img && !node.image) node.image = img;
          setEnrichedImage(img);
          setEnrichedDeezerId(deezerArtist.id);
        } else if (!cancelled) {
          setEnrichedImage(null);
        }
      })
      .catch(() => { if (!cancelled) setEnrichedImage(null); });

    return () => { cancelled = true; };
  }, [node]);

  // Determine the best available Deezer ID for track fetching
  const effectiveDeezerId = /^\d+$/.test(node?.id) ? node.id : enrichedDeezerId;

  useEffect(() => {
    if (!node) return;
    setListeners(null);

    let cancelled = false;

    getArtistInfo(node.name)
      .then((info) => {
        if (!cancelled && info) setListeners(info.listeners);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [node]);

  // Scroll fade indicator for expanded state
  useEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    const checkScroll = () => {
      const hasOverflow = el.scrollHeight > el.clientHeight + 5;
      const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 10;
      setShowScrollFade(hasOverflow && !atBottom);
    };
    checkScroll();
    el.addEventListener('scroll', checkScroll, { passive: true });
    const observer = new ResizeObserver(checkScroll);
    observer.observe(el);
    return () => {
      el.removeEventListener('scroll', checkScroll);
      observer.disconnect();
    };
  }, [expanded, allTracks]);

  // Lazy-load all songs when expanded
  useEffect(() => {
    if (!expanded || allTracks !== null || loadingTracks) return;
    const deezerId = effectiveDeezerId;
    if (!deezerId) return;

    let cancelled = false;
    setLoadingTracks(true);

    getArtistTopTracks(deezerId, 10, { priority: true })
      .then((tracks) => {
        if (!cancelled) setAllTracks(tracks.slice(0, 6));
      })
      .catch(() => {
        if (!cancelled) setAllTracks([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingTracks(false);
      });

    return () => { cancelled = true; };
  }, [expanded, effectiveDeezerId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggleExpand = () => {
    if (expanded && panelRef.current) {
      panelRef.current.scrollTop = 0;
    }
    setExpanded(prev => !prev);
  };

  const handleShare = useCallback(async () => {
    if (!node) return;
    const artistUrl = buildArtistPageUrl(node.name);

    if (navigator.share) {
      try {
        await navigator.share({
          title: `${node.name} \u2014 Stellar`,
          url: artistUrl,
        });
      } catch {
        // User cancelled or share failed — ignore
      }
      return;
    }

    // Fallback: copy URL to clipboard
    try {
      await navigator.clipboard.writeText(artistUrl);
    } catch {
      // Clipboard unavailable — ignore
    }
  }, [node]);

  if (!node) return null;

  const isSeed = node.type === 'seed';
  const badge = getBadgeInfo(node, listeners);

  return (
    <div className={`detail-panel${expanded ? ' expanded' : ''}`} ref={panelRef}>
      <div className="panel-header-actions">
        <button className="panel-share" onClick={handleShare} title="Share artist">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
            <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8" />
            <polyline points="16 6 12 2 8 6" />
            <line x1="12" y1="2" x2="12" y2="15" />
          </svg>
        </button>
        <button className="panel-close" onClick={onClose}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>

      <div className="panel-content">
        <div className="panel-artist-image-wrapper">
          {node.imageLarge || node.image || enrichedImage ? (
            <img
              className="panel-artist-image"
              src={node.imageLarge || node.image || enrichedImage}
              alt={node.name}
            />
          ) : (
            <div className="panel-artist-image panel-image-placeholder">
              {node.name.charAt(0)}
            </div>
          )}
        </div>

        <div className="panel-info-col">
          <h3 className="panel-artist-name">{node.name}</h3>

          <ArtistActionPills artistName={node.name} artistId={node.id || enrichedDeezerId} artistImage={node.image || enrichedImage} />

          <ActionPillsCallout />

          {node.zone && ZONE_INFO[node.zone] ? (() => {
            const zi = ZONE_INFO[node.zone];
            const zc = ZONE_COLORS[node.zone];
            const color = zc ? `hsl(${zc.h}, ${zc.s}%, ${zc.l}%)` : undefined;
            const bg = zc ? `hsla(${zc.h}, ${zc.s}%, ${zc.l}%, 0.15)` : undefined;
            return (
              <div className="panel-zone-row">
                <span className="panel-type-badge badge-zone" style={{ background: bg, color }}>
                  {zi.label}
                </span>
                <span className="panel-zone-desc" style={{ color }}>{zi.desc}</span>
              </div>
            );
          })() : !badge.pending && (
            <span className={`panel-type-badge ${badge.className}`}>
              {badge.label}
            </span>
          )}

          {/* Bridge note: shows which seeds this artist connects */}
          {node.discoveryMethod === 'bridge' && node.bridgeSeedNames && node.bridgeSeedNames.length > 0 && (
            <span className="panel-discovery-note" style={{ color: badge.color }}>
              Bridges {node.bridgeSeedNames.join(' & ')}
            </span>
          )}

          {/* Deep cut note: shows how this artist was discovered */}
          {node.discoveryMethod === 'deep_cut' && node.discoveredViaName && (
            <span className="panel-discovery-note" style={{ color: badge.color }}>
              Discovered via {node.discoveredViaName}
            </span>
          )}

          {/* Chain path visualization: shows the full chain with current artist highlighted */}
          {node.discoveryMethod === 'chain_bridge' && (() => {
            const path = node.chainPath && node.chainPath.length > 0
              ? node.chainPath
              : node.chainBridgeSeedNames && node.chainBridgeSeedNames.length >= 2
                ? [node.chainBridgeSeedNames[0], node.name, node.chainBridgeSeedNames[1]]
                : null;
            if (!path) return null;
            return (
              <div className="panel-chain-path">
                {path.map((name, i) => {
                  const isCurrent = name.toLowerCase().trim() === node.name.toLowerCase().trim();
                  const isSeedNode = i === 0 || i === path.length - 1;
                  return (
                    <span key={i} className="panel-chain-step">
                      {i > 0 && <span className="panel-chain-arrow">→</span>}
                      <span className={`panel-chain-name${isCurrent ? ' current' : ''}${isSeedNode ? ' seed' : ''}`}>
                        {name}
                      </span>
                    </span>
                  );
                })}
              </div>
            );
          })()}

          {/* Drift note: genre-adjacent outlier */}
          {(node.isDrift || node.tier === 'drift') && node.relatedSeedNames && node.relatedSeedNames.length > 0 && (
            <span className="panel-discovery-note" style={{ color: badge.color }}>
              Genre-adjacent to {node.relatedSeedNames.join(' & ')}
            </span>
          )}

          {/* Standard recommendation / hidden gem note (only for types without a specific note above) */}
          {node.type !== 'seed' &&
           !node.isDrift && node.tier !== 'drift' &&
           node.discoveryMethod !== 'bridge' &&
           node.discoveryMethod !== 'deep_cut' &&
           node.discoveryMethod !== 'chain_bridge' &&
           node.relatedSeedNames && node.relatedSeedNames.length > 0 && (
            <span className="panel-discovery-note" style={{ color: badge.color }}>
              Similar to {node.relatedSeedNames.join(' & ')}
            </span>
          )}

          {listeners > 0 && (
            <div className="panel-listeners">
              {listeners.toLocaleString()} listeners
            </div>
          )}
        </div>

        {/* Expand/collapse toggle — visible on both desktop and mobile */}
        <button className="panel-expand-toggle" onClick={handleToggleExpand}>
          <span>{expanded ? 'Show less' : 'Show more'}</span>
          <svg className={`panel-expand-chevron${expanded ? ' rotated' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>

        <div className="panel-body">
        {node.genres && node.genres.length > 0 && (
          <div className="panel-genres">
            {node.genres.slice(0, 5).map((genre) => (
              <span key={genre} className="genre-tag">{genre}</span>
            ))}
          </div>
        )}

        <div className="panel-actions">
          {loadingTracks && (
            <div className="panel-loading-track">Loading songs...</div>
          )}

          {allTracks && allTracks.length > 0 && (
            <div className="panel-more-tracks">
              {allTracks.map((track) => {
                const isTrackPlaying = audio.isPlaying && audio.currentTrack?.id === track.id;
                return (
                  <div key={track.id} className="panel-track-row">
                    {track.albumImage ? (
                      <img className="panel-track-row-art" src={track.albumImage} alt="" loading="lazy" />
                    ) : (
                      <div className="panel-track-row-art-placeholder" />
                    )}
                    <div className="panel-track-row-info">
                      <span className="panel-track-row-name">{track.name}</span>
                      <span className="panel-track-row-album">{track.albumName}</span>
                    </div>
                    <div className="panel-action-row">
                      {track.previewUrl && (
                        <button
                          className={`panel-track-row-play ${isTrackPlaying ? 'playing' : ''}`}
                          onClick={() => {
                            if (isTrackPlaying) {
                              audio.pause();
                            } else {
                              audio.play(track);
                            }
                          }}
                          title={isTrackPlaying ? 'Pause' : 'Play preview'}
                        >
                          {audio.currentTrack?.id === track.id && audio.progress > 0 && (
                            <svg className="preview-progress-ring preview-progress-ring--sm" viewBox="0 0 40 40">
                              <circle
                                cx="20" cy="20" r="18"
                                fill="none"
                                stroke="rgba(129, 140, 248, 0.25)"
                                strokeWidth="2"
                              />
                              <circle
                                cx="20" cy="20" r="18"
                                fill="none"
                                stroke="#818cf8"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeDasharray={2 * Math.PI * 18}
                                strokeDashoffset={2 * Math.PI * 18 * (1 - (audio.progress || 0))}
                              />
                            </svg>
                          )}
                          {isTrackPlaying ? (
                            <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                              <rect x="6" y="4" width="4" height="16" />
                              <rect x="14" y="4" width="4" height="16" />
                            </svg>
                          ) : (
                            <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                              <polygon points="6,3 20,12 6,21" />
                            </svg>
                          )}
                        </button>
                      )}
                      <a
                        className={`panel-track-row-spotify${musicService === 'apple-music' ? ' apple-music' : ''}`}
                        href={appleMusicLinks.get(trackLinkKey(track)) || buildMusicSearchUrl(track, musicService)}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={`Search in ${MUSIC_SERVICE_LABELS[musicService]}`}
                      >
                        <MusicServiceIcon service={musicService} size={16} />
                      </a>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {allTracks && allTracks.length === 0 && (
            <div className="panel-no-preview">No songs found</div>
          )}

          {!isSeed && onQueueSeed && allTracks && allTracks.length > 0 && (() => {
            const isQueued = pendingSeeds.some((s) => s.id === node.id);
            return (
              <button
                className={`panel-seed-wide ${isQueued ? 'queued' : ''}`}
                onClick={() => isQueued ? onUnqueueSeed(node.id) : onQueueSeed(node)}
              >
                {isQueued ? (
                  <>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="14" height="14">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    Queued as seed
                  </>
                ) : (
                  <>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="14" height="14">
                      <line x1="12" y1="5" x2="12" y2="19" />
                      <line x1="5" y1="12" x2="19" y2="12" />
                    </svg>
                    Add as seed
                  </>
                )}
              </button>
            );
          })()}
        </div>
        </div>
      </div>

      {showScrollFade && (
        <div className="panel-scroll-fade">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>
      )}
    </div>
  );
}

export default ArtistDetailPanel;
